import {
  type ChangePasswordRequest,
  type ForgotPasswordRequest,
  type LoginRequest,
  type LoginResponse,
  LoginResponseSchema,
  type ResetPasswordRequest,
  type UpdatePreferencesRequest,
  type UserDto,
  UserDtoSchema,
} from '@gestao/shared'
import { resetRevealHistory } from '@gestao/ui'

import { api, refreshSession } from '../../lib/api'
import { queryClient } from '../../lib/query-client'
import { session } from '../../lib/session'
import { broadcastSession, broadcastSignOut } from '../../lib/session-sync'

/** Chamadas de autenticação (contrato em docs/sprint-0-spec.md §3.2). */
export const authApi = {
  login: (body: LoginRequest) =>
    api.post<LoginResponse>('/auth/login', body, { auth: false, schema: LoginResponseSchema }),
  logout: () => api.post('/auth/logout'),
  me: () => api.get<UserDto>('/auth/me', { schema: UserDtoSchema }),
  updatePreferences: (body: UpdatePreferencesRequest) =>
    api.patch<UserDto>('/auth/me/preferences', body, { schema: UserDtoSchema }),
  changePassword: (body: ChangePasswordRequest) => api.post('/auth/change-password', body),
  forgotPassword: (body: ForgotPasswordRequest) =>
    api.post('/auth/forgot-password', body, { auth: false }),
  resetPassword: (body: ResetPasswordRequest) =>
    api.post('/auth/reset-password', body, { auth: false }),
  /** Confere o link de redefinição sem consumi-lo (404 `AUTH_TOKEN_INVALID` se não vale). */
  resetTokenStatus: (token: string, signal?: AbortSignal) =>
    api.get<{ valid: true }>(`/auth/reset-password/${encodeURIComponent(token)}`, {
      auth: false,
      signal,
    }),
}

/** Formato do token aceito pela API (o resto nem chega a ser consultado). */
export const RESET_TOKEN_PATTERN = /^[\w-]{16,200}$/

/** Entra e compartilha a sessão com as outras abas. */
export async function signIn(body: LoginRequest): Promise<LoginResponse> {
  const response = await authApi.login(body)
  session.signIn(response)
  broadcastSession(response)
  return response
}

/** Limpa tudo o que é do usuário no navegador (cache de consultas, histórico de animação). */
function clearClientState(): void {
  queryClient.clear()
  resetRevealHistory()
}

/** Sai: revoga a sessão na API (se possível) e limpa o estado local de qualquer jeito. */
export async function signOut(): Promise<void> {
  try {
    await authApi.logout()
  } catch {
    // Sessão já inválida ou API fora: o estado local é limpo mesmo assim.
  } finally {
    session.end('signed-out')
    broadcastSignOut()
    clearClientState()
  }
}

/** Encerra só a sessão local (por exemplo, depois de redefinir a senha por link). */
export function forgetLocalSession(): void {
  if (session.get().status === 'authenticated') {
    session.end('signed-out')
    clearClientState()
  }
}

let restoring: Promise<void> | null = null

/**
 * Restaura a sessão ao abrir o app (POST /auth/refresh pelo cookie). Uma vez por carga da
 * página, mesmo com o StrictMode ou chamadas repetidas do loader.
 */
export function restoreSession(): Promise<void> {
  restoring ??= refreshSession(null).then(
    (result) => {
      if (!result) session.end('required')
    },
    () => {
      // Sem resposta da API: segue sem sessão; o login mostra o erro ao tentar entrar.
      session.end('required')
    },
  )
  return restoring
}

/** Só para testes. */
export function resetRestoreSession(): void {
  restoring = null
}
